import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, X, Info, ExternalLink, Check, Clock, UserPlus, Mail, Phone,
  Building2, MapPin, Send, Loader2
} from "lucide-react";
import PhoneInputWithCode from "@/components/PhoneInputWithCode";

const RECOMMENDATION_TYPES = [
  "Instructor",
  "Occupational Therapy Clinician",
  "Other Clinician",
  "Physical Therapy Clinician",
  "Professional (Non-Clinician)",
  "Professional (Other)",
  "Supervisor",
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR","GU","VI","AS","MP"
];

const EMPTY_FORM = {
  first_name: "", last_name: "", recommendation_type: "", work_email: "",
  work_phone: "", place_of_employment: "", work_city: "", work_state: "", work_zip: ""
};

export default function RecommendationSection({ recommendations = [], setRecommendations, requiredCount = 2, appId }) {
  const { token } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const completedCount = recommendations.filter(r => r.request_stage === "Completed").length;
  const progressPct = Math.min((completedCount / requiredCount) * 100, 100);

  const validateForm = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = true;
    if (!form.last_name.trim()) e.last_name = true;
    if (!form.recommendation_type) e.recommendation_type = true;
    if (!form.work_email.trim() || !/\S+@\S+\.\S+/.test(form.work_email)) e.work_email = true;
    if (!form.work_phone.trim()) e.work_phone = true;
    if (!form.place_of_employment.trim()) e.place_of_employment = true;
    if (!form.work_city.trim()) e.work_city = true;
    if (!form.work_state) e.work_state = true;
    if (!form.work_zip.trim()) e.work_zip = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
    const newRec = {
      id: Date.now().toString(),
      ...form,
      request_stage: "Pending",
      date_requested: now,
      date_confirmed: null,
    };
    setRecommendations([...recommendations, newRec]);
    setForm({ ...EMPTY_FORM });
    setErrors({});
    setShowForm(false);
    toast.success("Recommendation added. Send the email request to notify your recommender.");
  };

  const handleSendEmail = async (rec) => {
    if (!appId || !token) return;
    setSendingId(rec.id);
    try {
      // First save the current recommendations to MongoDB so the send endpoint can find it
      await axios.put(
        `${API}/applications/${appId}`,
        { recommendations },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const response = await axios.post(
        `${API}/applications/${appId}/recommendations/${rec.id}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;

      // Update local state
      setRecommendations(prev =>
        prev.map(r => r.id === rec.id ? { ...r, request_stage: "Sent", token: data.token } : r)
      );

      // Open mock email in new tab
      const params = new URLSearchParams({
        recommenderName: `${rec.first_name} ${rec.last_name}`,
        applicantName: data.applicant_name,
        program: data.program,
        portalToken: data.token,
        advisorName: data.advisor_name,
        advisorEmail: data.advisor_email,
        advisorTitle: data.advisor_title,
        recommenderEmail: rec.work_email,
      });
      window.open(
        `${window.location.origin}/mock-recommendation-email?${params.toString()}`,
        "_blank"
      );
      toast.success("Recommendation request sent!");
    } catch (err) {
      console.error("Send recommendation error:", err);
      toast.error("Failed to send recommendation request");
    } finally {
      setSendingId(null);
    }
  };

  const handleRemove = (id) => {
    setRecommendations(recommendations.filter(r => r.id !== id));
  };

  const getStatusConfig = (stage) => {
    switch (stage) {
      case "Completed":
        return { color: "#739600", bg: "bg-[#739600]/15", border: "border-[#739600]/30", icon: Check, label: "Completed" };
      case "Sent":
        return { color: "#00677F", bg: "bg-[#00677F]/15", border: "border-[#00677F]/30", icon: Send, label: "Sent" };
      default:
        return { color: "#FF9800", bg: "bg-[#FF9800]/15", border: "border-[#FF9800]/30", icon: Clock, label: "Pending" };
    }
  };

  const inputCls = (name) => `h-11 bg-black/30 border-white/[0.08] text-white rounded-xl px-4 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600 ${errors[name] ? "border-red-400/50" : ""}`;
  const labelCls = "text-sm text-slate-300 flex items-center gap-1.5 mb-1.5";

  return (
    <div className="space-y-8" data-testid="recommendation-section">

      {/* === Top Hero Card === */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00677F]/20 via-transparent to-[#7B68EE]/10" />
        <div className="relative p-8">
          <div className="flex items-start justify-between gap-8">
            <div className="flex-1 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00677F]/20 border border-[#00677F]/30">
                <UserPlus className="w-3.5 h-3.5 text-[#A1D8E0]" />
                <span className="text-[#A1D8E0] text-xs font-medium tracking-wide uppercase">Professional References</span>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed max-w-2xl">
                Applicants are required to submit recommendations. To be sure that you are submitting the proper number and type of recommendations required, please review the Admissions Requirements for your particular program at{" "}
                <a href="https://www.usa.edu" target="_blank" rel="noopener noreferrer" className="text-[#A1D8E0] hover:text-white transition-colors inline-flex items-center gap-1 underline underline-offset-2 decoration-[#A1D8E0]/30">
                  www.usa.edu <ExternalLink className="w-3 h-3" />
                </a>
                , ask Journey, or consult your Advisor.
              </p>
              <p className="text-slate-500 text-xs italic">
                References must be professional in nature — Clinical, Professional, Academic, or Supervisor.
              </p>
            </div>
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div className="relative w-24 h-24">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                  <circle
                    cx="50" cy="50" r="40" fill="none"
                    stroke={completedCount >= requiredCount ? "#739600" : "#00677F"}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${progressPct * 2.51} 251`}
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-2xl font-bold ${completedCount >= requiredCount ? "text-[#739600]" : "text-white"}`}>
                    {completedCount}
                  </span>
                  <span className="text-slate-500 text-[10px] uppercase tracking-wider">of {requiredCount}</span>
                </div>
              </div>
              <span className="text-slate-500 text-[10px] uppercase tracking-wider">Completed</span>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t border-white/[0.05]">
            {[
              "Add Recommender",
              "Send Email Request",
              "Recommender Fills Form",
              "Status: Completed"
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2 flex-1">
                <div className="w-5 h-5 rounded-full bg-[#00677F]/30 border border-[#00677F]/40 flex items-center justify-center shrink-0">
                  <span className="text-[10px] text-[#A1D8E0] font-bold">{i + 1}</span>
                </div>
                <span className="text-slate-400 text-xs">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* === Add Button === */}
      <Button
        onClick={() => setShowForm(true)}
        className="bg-[#00677F] hover:bg-[#135163] text-white rounded-xl px-6 h-11 shadow-[0_0_20px_rgba(0,103,127,0.3)] hover:shadow-[0_0_30px_rgba(0,103,127,0.4)] transition-all"
        data-testid="add-recommendation-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Recommendation
      </Button>

      {/* === Recommendation Cards === */}
      {recommendations.length > 0 ? (
        <div className="space-y-3">
          {recommendations.map((rec) => {
            const status = getStatusConfig(rec.request_stage);
            const StatusIcon = status.icon;
            return (
              <div
                key={rec.id}
                className="group relative overflow-hidden rounded-2xl border border-white/[0.06] hover:border-white/[0.12] transition-all duration-300"
                data-testid={`rec-row-${rec.id}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex items-center gap-5 p-5">
                  {/* Status Badge */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${status.bg} border ${status.border}`}>
                    <StatusIcon className="w-4 h-4" style={{ color: status.color }} />
                  </div>

                  {/* Name & Type */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-medium text-sm">
                        {rec.first_name} {rec.last_name}
                      </h4>
                      <span className="px-2 py-0.5 rounded-full bg-[#7B68EE]/10 border border-[#7B68EE]/20 text-[#7B68EE] text-[10px] font-medium uppercase tracking-wider">
                        {rec.recommendation_type}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${status.bg} border ${status.border}`}
                        style={{ color: status.color }}
                        data-testid={`rec-status-${rec.id}`}
                      >
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5">
                      <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                        <Building2 className="w-3 h-3" /> {rec.place_of_employment}
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                        <Mail className="w-3 h-3" /> {rec.work_email}
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                        <MapPin className="w-3 h-3" /> {rec.work_city}, {rec.work_state}
                      </span>
                    </div>
                  </div>

                  {/* Action / Dates */}
                  <div className="flex items-center gap-3 shrink-0">
                    {rec.request_stage === "Pending" && (
                      <Button
                        size="sm"
                        onClick={() => handleSendEmail(rec)}
                        disabled={sendingId === rec.id}
                        className="bg-[#00677F] hover:bg-[#135163] text-white rounded-full px-4 h-9 text-xs shadow-[0_0_12px_rgba(0,103,127,0.3)]"
                        data-testid={`rec-send-${rec.id}`}
                      >
                        {sendingId === rec.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Send Email
                      </Button>
                    )}
                    {rec.request_stage === "Sent" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendEmail(rec)}
                        disabled={sendingId === rec.id}
                        className="border-[#00677F]/40 text-[#A1D8E0] hover:bg-[#00677F]/10 rounded-full px-4 h-9 text-xs"
                        data-testid={`rec-resend-${rec.id}`}
                      >
                        {sendingId === rec.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Mail className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Resend
                      </Button>
                    )}
                    <div className="text-right space-y-1">
                      <div className="text-slate-500 text-xs">
                        Requested <span className="text-slate-400">{rec.date_requested}</span>
                      </div>
                      {rec.date_confirmed && (
                        <div className="text-slate-500 text-xs">
                          Completed <span className="text-[#739600]">{new Date(rec.date_confirmed).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Remove (only for Pending) */}
                  {rec.request_stage === "Pending" && (
                    <button
                      onClick={() => handleRemove(rec.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-red-400 p-1"
                      data-testid={`rec-remove-${rec.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-white/[0.08] p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-6 h-6 text-slate-600" />
          </div>
          <p className="text-slate-500 text-sm">No recommendations yet</p>
          <p className="text-slate-600 text-xs mt-1">Click "Add Recommendation" to get started</p>
        </div>
      )}

      {/* === Modal === */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="recommendation-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowForm(false); setErrors({}); }} />
          <div className="relative w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl shadow-black/40 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="relative overflow-hidden px-6 py-5">
              <div className="absolute inset-0 bg-gradient-to-r from-[#00677F] to-[#00677F]/80" />
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImEiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHBhdGggZD0iTTAgMGgyMHYyMEgweiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjEwIiBjeT0iMTAiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjYSkiLz48L3N2Zz4=')] opacity-50" />
              <div className="relative flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                  <UserPlus className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-white text-lg font-semibold">Recommendation Request</h4>
                  <p className="text-white/60 text-xs">Fill in your recommender's details</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="bg-[#0f1419] border-x border-white/[0.08] p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> First Name</Label>
                  <Input placeholder="First Name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls("first_name")} data-testid="rec-first_name" />
                </div>
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Last Name</Label>
                  <Input placeholder="Last Name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls("last_name")} data-testid="rec-last_name" />
                </div>
              </div>

              <div>
                <Label className={labelCls}><span className="text-red-400">*</span> Recommendation Type</Label>
                <Select value={form.recommendation_type} onValueChange={(v) => setForm({ ...form, recommendation_type: v })}>
                  <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl focus:border-[#A1D8E0] ${errors.recommendation_type ? "border-red-400/50" : ""}`} data-testid="rec-type-select">
                    <SelectValue placeholder="Select Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1419] border-white/10">
                    {RECOMMENDATION_TYPES.map(t => (
                      <SelectItem key={t} value={t} className="text-slate-300 hover:bg-white/5">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Work Email</Label>
                  <Input type="email" placeholder="email@work.com" value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} className={inputCls("work_email")} data-testid="rec-work_email" />
                </div>
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Work Phone</Label>
                  <PhoneInputWithCode
                    value={form.work_phone}
                    countryCode={form.work_phone_country_code || "+1|US"}
                    onPhoneChange={(val) => setForm({ ...form, work_phone: val })}
                    onCodeChange={(val) => setForm({ ...form, work_phone_country_code: val })}
                    testId="rec-work_phone"
                    theme="dark"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Place of Employment</Label>
                  <Input placeholder="Organization" value={form.place_of_employment} onChange={(e) => setForm({ ...form, place_of_employment: e.target.value })} className={inputCls("place_of_employment")} data-testid="rec-place_of_employment" />
                </div>
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Work City</Label>
                  <Input placeholder="City" value={form.work_city} onChange={(e) => setForm({ ...form, work_city: e.target.value })} className={inputCls("work_city")} data-testid="rec-work_city" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Work State</Label>
                  <Select value={form.work_state} onValueChange={(v) => setForm({ ...form, work_state: v })}>
                    <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl focus:border-[#A1D8E0] ${errors.work_state ? "border-red-400/50" : ""}`} data-testid="rec-state-select">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1419] border-white/10 max-h-[200px]">
                      {US_STATES.map(s => (
                        <SelectItem key={s} value={s} className="text-slate-300 hover:bg-white/5">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Work Zip</Label>
                  <Input placeholder="00000" value={form.work_zip} onChange={(e) => setForm({ ...form, work_zip: e.target.value })} className={inputCls("work_zip")} data-testid="rec-work_zip" />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-[#0f1419] border border-white/[0.08] border-t-0 rounded-b-2xl px-6 pb-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowForm(false); setErrors({}); setForm({ ...EMPTY_FORM }); }}
                className="border-white/20 text-white hover:bg-white/5 rounded-xl px-6"
                data-testid="rec-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-[#00677F] hover:bg-[#135163] text-white rounded-xl px-6 shadow-[0_0_15px_rgba(0,103,127,0.3)]"
                data-testid="rec-submit-btn"
              >
                Add Recommender
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
